// Landing page product tour ("Everything a world needs").
// Real screenshots (public/landing/*.webp) in device frames, scroll-driven
// reveals and parallax, a devices section, and a closing call to action.
// Motion is skipped for prefers-reduced-motion (framer-motion's useReducedMotion).
import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  Wand2, UserRound, Boxes, Gamepad2, Glasses, Smartphone, Laptop, Monitor, Check, ArrowRight,
  PenTool, Trophy, Footprints, Layers, Share2,
} from 'lucide-react';
import { COPY, type Look } from './looks';
import './Showcase.css';


interface Feature {
  id: string;
  num: string;
  icon: React.ReactNode;
  eyebrow: string;
  points: string[];
  shot: string;
  alt: string;
  badges: { icon: React.ReactNode; text: string; pos: 'tl' | 'tr' | 'bl' | 'br' }[];
  accent: string;
}

const FEATURES: Feature[] = [
  {
    id: 'studio', num: '01', icon: <Wand2 size={16} />, eyebrow: 'GathrXR Studio',
    points: ['Whole rooms or single props', 'Start from a prompt or a reference image', 'Drops straight into your world'],
    shot: '/landing/studio.webp', alt: 'GathrXR Studio generating a 3D world from a prompt',
    badges: [{ icon: <PenTool size={13} />, text: 'Sketch, build, check', pos: 'tr' }, { icon: <Check size={13} />, text: 'World ready', pos: 'bl' }],
    accent: '#ffd166',
  },
  {
    id: 'avatars', num: '02', icon: <UserRound size={16} />, eyebrow: 'Avatar creator',
    points: ['16 starter looks', 'Walk, run, dance and emotes built in', 'Keep a whole wardrobe and switch anytime'],
    shot: '/landing/avatar-creator.webp', alt: 'The GathrXR avatar creator',
    badges: [{ icon: <Footprints size={13} />, text: 'Rigged & animated', pos: 'tl' }, { icon: <Layers size={13} />, text: 'Wardrobe', pos: 'br' }],
    accent: '#b78cff',
  },
  {
    id: 'builder', num: '03', icon: <Boxes size={16} />, eyebrow: 'In-browser builder',
    points: ['Model library, skyboxes and lighting', 'Undo, snap and precise transforms', 'Build in VR with your hands'],
    shot: '/landing/builder.webp', alt: 'The GathrXR world builder',
    badges: [{ icon: <Share2 size={13} />, text: 'Share one link', pos: 'tr' }, { icon: <Glasses size={13} />, text: 'Edit in VR too', pos: 'bl' }],
    accent: '#00e5ff',
  },
  {
    id: 'games', num: '04', icon: <Gamepad2 size={16} />, eyebrow: 'Game night',
    points: ['8 multiplayer games', 'Leaderboards for every game', 'Trivia with 1,000+ questions'],
    shot: '/landing/games.webp', alt: 'A multiplayer card game in a GathrXR world',
    badges: [{ icon: <Trophy size={13} />, text: 'Leaderboards', pos: 'tl' }, { icon: <Gamepad2 size={13} />, text: '2–20 players', pos: 'br' }],
    accent: '#00c2b3',
  },
];

function Reveal({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-12% 0px' }}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function BrowserFrame({ src, alt, badges, accent }: { src: string; alt: string; badges: Feature['badges']; accent: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [24, -24]);
  const rotate = useTransform(scrollYProgress, [0, 0.5, 1], reduce ? [0, 0, 0] : [4, 0, -2]);
  return (
    <div ref={ref} className="sc-shot-wrap" style={{ '--sc-accent': accent } as React.CSSProperties}>
      <motion.div className="sc-browser" style={{ y, rotateX: rotate }}>
        <div className="sc-browser-bar"><i /><i /><i /><span>gathrxr.com</span></div>
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      </motion.div>
      {badges.map((b, i) => (
        <motion.div
          key={b.text}
          className={`sc-badge sc-badge-${b.pos}`}
          initial={reduce ? false : { opacity: 0, scale: 0.85, y: 12 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35 + i * 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          {b.icon}{b.text}
        </motion.div>
      ))}
    </div>
  );
}

const NPC_CAST = [
  { img: 'south-asian-woman', name: 'Priya', move: 'Waving' },
  { img: 'black-man', name: 'Marcus', move: 'Walking around' },
  { img: 'latino-man', name: 'Diego', move: 'Dancing' },
  { img: 'asian-woman', name: 'Mei', move: 'On the phone' },
];

const DEVICES = [
  { icon: <Glasses size={22} />, name: 'VR headsets', detail: 'Meta Quest and any WebXR headset, with hand tracking' },
  { icon: <Monitor size={22} />, name: 'Desktop', detail: 'Chrome, Edge, Safari or Firefox. Keyboard and mouse, nothing to install' },
  { icon: <Laptop size={22} />, name: 'Laptop', detail: 'Same worlds, same friends, wherever you are' },
  { icon: <Smartphone size={22} />, name: 'Phone & tablet', detail: 'Touch controls on iOS and Android' },
];

const STATS = [
  { n: '8', l: 'multiplayer games' },
  { n: '16', l: 'starter avatar looks' },
  { n: '1,000+', l: 'trivia questions' },
  { n: '0', l: 'downloads to install' },
];

// Two-line heading: the second line gets the accent treatment (if the look has one).
const Two = ({ t }: { t: [string, string] }) => (
  <>{t[0]}{t[1] && <><br /><span className="sc-grad">{t[1]}</span></>}</>
);

const Showcase: React.FC<{ onStart: () => void; gallery?: React.ReactNode; look?: Look }> = ({ onStart, gallery, look = 'bold' }) => {
  const c = COPY[look];
  return (
  <div className="sc" id="tour">
    {/* Statement */}
    <section className="sc-intro">
      <Reveal>
        <span className="sc-eyebrow">What you get</span>
        <h2 className="sc-mega sc-mega-xs"><Two t={c.introTitle} /></h2>
        <p className="sc-lede">{c.introLede}</p>
      </Reveal>
    </section>

    {/* Feature tour, 2 × 2 */}
    <div className="sc-grid">
      {FEATURES.map((f, i) => (
        <Reveal key={f.id} className="sc-card" delay={(i % 2) * 0.12}>
          <article id={`feature-${f.id}`} style={{ '--sc-accent': f.accent } as React.CSSProperties}>
            <BrowserFrame src={f.shot} alt={f.alt} badges={f.badges} accent={f.accent} />
            <div className="sc-card-body">
              <div className="sc-card-top">
                <span className="sc-num">{f.num}</span>
                <span className="sc-tag">{f.icon}{f.eyebrow}</span>
              </div>
              <h3 className="sc-title">{c.features[f.id].title[0]}{c.features[f.id].title[1] && <><br /><em>{c.features[f.id].title[1]}</em></>}</h3>
              <p className="sc-text">{c.features[f.id].copy}</p>
              <ul className="sc-points">
                {f.points.map((p) => <li key={p}><Check size={15} strokeWidth={2.6} />{p}</li>)}
              </ul>
            </div>
          </article>
        </Reveal>
      ))}
    </div>

    {/* Bring your own world */}
    <section className="sc-import" id="import">
      <Reveal className="sc-import-shot">
        <img src="/landing/import.webp" alt="A 3D scene going from untextured model to a finished, lit GathrXR world" loading="lazy" decoding="async" />
      </Reveal>
      <Reveal className="sc-import-body" delay={0.1}>
        <span className="sc-num">05</span>
        <h3 className="sc-title">{c.importTitle ?? 'Bring your own world.'}</h3>
        <p className="sc-text">{c.importCopy ?? COPY.tech.importCopy}</p>
        <div className="sc-import-logos">
          <span><img src="/landing/logos/blender.svg" alt="" />Blender</span>
          <span><img src="/landing/logos/unity.svg" alt="" />Unity</span>
          <span className="sc-import-fmt">.glb / .gltf</span>
        </div>
        <ul className="sc-points">
          <li><Check size={15} strokeWidth={2.6} />Blender: File › Export › glTF 2.0</li>
          <li><Check size={15} strokeWidth={2.6} />Unity: export with the UnityGLTF package</li>
          <li><Check size={15} strokeWidth={2.6} />Or any tool that writes .glb or .gltf</li>
        </ul>
      </Reveal>
    </section>

    {/* NPCs */}
    <section className="sc-import sc-npc" id="npcs">
      <Reveal className="sc-npc-stage" delay={0.1}>
        {NPC_CAST.map((n) => (
          <figure key={n.img} className="sc-npc-fig">
            <figcaption><b>{n.name}</b><span>{n.move}</span></figcaption>
            <img src={`/landing/crew/${n.img}.webp`} alt="" loading="lazy" decoding="async" />
          </figure>
        ))}
        <div className="sc-npc-floor" />
      </Reveal>
      <Reveal className="sc-import-body">
        <span className="sc-num">06</span>
        <h3 className="sc-title">NPCs that live in your world.</h3>
        <p className="sc-text">Add a character from the builder: choose gender, background, type and outfit, give them a name and a move. They stand around, dance, wave or wander the space, and every visitor sees them in the same place.</p>
        <ul className="sc-points">
          <li><Check size={15} strokeWidth={2.6} />Built on the same avatar pipeline as yours</li>
          <li><Check size={15} strokeWidth={2.6} />25+ looping moves, or walk around a set area</li>
          <li><Check size={15} strokeWidth={2.6} />Name tags, just like real players</li>
        </ul>
        <div className="sc-import-logos sc-npc-soon">
          <span>Coming soon</span>
          <span>Conversation</span>
          <span>Voice</span>
          <span>AI personality</span>
        </div>
      </Reveal>
    </section>

    {/* Build with your AI agent (MCP) */}
    <section className="sc-import sc-agents" id="agents">
      <Reveal className="sc-import-shot">
        <img src="/landing/agents.webp" alt="An AI chat window sending streams of light that assemble a floating 3D world with a stage, palms and a fountain" loading="lazy" decoding="async" />
      </Reveal>
      <Reveal className="sc-import-body" delay={0.1}>
        <span className="sc-num">07</span>
        <h3 className="sc-title">Build worlds with your AI agent.</h3>
        <p className="sc-text">Connect ChatGPT, Claude or Gemini to GathrXR over MCP and describe what you want. Your agent plans the scene, places shapes, models, lights and a sky, and the world appears in your dashboard, ready to edit by hand or explore in VR.</p>
        <div className="sc-import-logos">
          <span>ChatGPT</span>
          <span>Claude</span>
          <span>Gemini</span>
          <span className="sc-import-fmt">MCP</span>
        </div>
        <ul className="sc-points">
          <li><Check size={15} strokeWidth={2.6} />One link to connect, or an API key for any MCP client</li>
          <li><Check size={15} strokeWidth={2.6} />"Build me a rooftop lounge at sunset" becomes a real world</li>
          <li><Check size={15} strokeWidth={2.6} />Edit existing worlds: add models, swap the sky, relight</li>
        </ul>
        <a className="sc-agents-cta" href="/developer">Connect your agent <ArrowRight size={16} /></a>
      </Reveal>
    </section>

    {/* Devices */}
    <section className="sc-devices" id="devices">
      <Reveal className="sc-devices-head">
        <span className="sc-eyebrow">Every screen</span>
        <h2 className="sc-mega sc-mega-sm"><Two t={c.devicesTitle} /></h2>
        <p className="sc-lede">{c.devicesLede}</p>
      </Reveal>
      <div className="sc-device-stage">
        <Reveal className="sc-dev sc-dev-desktop" delay={0}>
          <div className="sc-monitor"><img src="/landing/explore.webp" alt="A GathrXR world on desktop" loading="lazy" /></div>
          <div className="sc-monitor-stand" />
        </Reveal>
        <Reveal className="sc-dev sc-dev-headset" delay={0.15}>
          <div className="sc-headset"><img src="/landing/vr.webp" alt="A GathrXR world in a VR headset" loading="lazy" /></div>
        </Reveal>
        <Reveal className="sc-dev sc-dev-phone" delay={0.3}>
          <div className="sc-phone"><img src="/landing/phone.webp" alt="A GathrXR world on a phone" loading="lazy" /></div>
        </Reveal>
      </div>
      <div className="sc-device-list">
        {DEVICES.map((d, i) => (
          <Reveal key={d.name} className="sc-device" delay={i * 0.08}>
            <span className="sc-device-icon">{d.icon}</span>
            <div><b>{d.name}</b><p>{d.detail}</p></div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Featured worlds (curated; renders nothing when none are featured) */}
    {gallery && (
      <section className="sc-gallery" id="worlds">
        <Reveal className="sc-gallery-head">
          <span className="sc-eyebrow">Step inside</span>
          <h2 className="sc-mega sc-mega-sm"><Two t={c.galleryTitle} /></h2>
        </Reveal>
        {gallery}
      </section>
    )}

    {/* Stats */}
    <section className="sc-stats">
      {STATS.map((s, i) => (
        <Reveal key={s.l} className="sc-stat" delay={i * 0.08}>
          <span className="sc-stat-n">{s.n}</span>
          <span className="sc-stat-l">{s.l}</span>
        </Reveal>
      ))}
    </section>

    {/* Founder note (editorial look only) */}
    {look === 'editorial' && (
      <section className="sc-note">
        <Reveal>
          <p className="sc-note-text">
            I wanted a place where my family and friends could hang out together without everyone needing
            the same console, the same app or the same city. So I built one. Make yours, and bring your people.
          </p>
          <p className="sc-note-sig">Aquarius Maximus<span>Founder, GathrXR</span></p>
        </Reveal>
      </section>
    )}

    {/* Closing CTA */}
    <section className="sc-final">
      <Reveal>
        <h2 className="sc-mega"><Two t={c.finalTitle} /></h2>
        <button className="sc-final-btn" onClick={onStart}>{c.finalBtn} <ArrowRight size={20} /></button>
      </Reveal>
    </section>
  </div>
  );
};

export default Showcase;
